/**
 * Hook: Mint setup — initialize mint Nostr identity and Monero wallet.
 */

import { useState, useCallback } from 'react';
import { generateSecretKey, getPublicKey, nip19, finalizeEvent } from 'nostr-tools';
import { bytesToHex } from '@noble/hashes/utils';
import { useNostr } from '@nostrify/react';

import { createNewWallet as createMoneroWallet } from '@/lib/monero/wallet';
import { saveMintConfig, completeMintSetup, getMintConfig, isMintSetupComplete } from '@/lib/storage/mint-store';
import { createNewKeyset, type KeysetPrivate } from '@/lib/cashu/keyset';
import { saveKeyset } from '@/lib/storage/mint-store';
import { createMintAnnouncementEvent, type MintAnnouncement } from '@/lib/nostr/mint-announcement';

export interface MintSetupState {
  /** Nostr nsec (hex) */
  nsecHex: string;
  /** Nostr npub (hex) */
  npubHex: string;
  /** Nostr nsec (bech32) */
  nsecBech32: string;
  /** Monero seed phrase */
  moneroSeed: string;
  /** Monero primary address */
  moneroAddress: string;
  /** Monero view key (hex) */
  moneroViewKey: string;
  /** Monero spend key (hex) */
  moneroSpendKey: string;
  /** Monero node URI */
  nodeUri: string;
}

export function useMintSetup() {
  const [loading, setLoading] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  const [setupState, setSetupState] = useState<MintSetupState | null>(null);
  const { nostr } = useNostr();

  /** Check if setup is already complete */
  const checkSetup = useCallback(async (): Promise<boolean> => {
    const done = await isMintSetupComplete();
    setSetupComplete(done);
    return done;
  }, []);

  /** Step 1: Generate Nostr identity for the mint */
  const generateNostrIdentity = useCallback((): { nsecHex: string; npubHex: string; nsecBech32: string } => {
    const secretKey = generateSecretKey();
    const nsecHex = bytesToHex(secretKey);
    const npubHex = getPublicKey(secretKey);
    const nsecBech32 = nip19.nsecEncode(secretKey);

    return { nsecHex, npubHex, nsecBech32 };
  }, []);

  /** Step 2: Create Monero wallet (no sync needed for new wallets) */
  const generateMoneroWallet = useCallback(async (nodeUri: string): Promise<{
    seed: string; address: string; viewKey: string; spendKey: string;
  }> => {
    const config = await createMoneroWallet(nodeUri);
    return {
      seed: config.seed,
      address: config.address,
      viewKey: config.viewKey,
      spendKey: config.spendKey,
    };
  }, []);

  /** Step 3: Generate initial keyset */
  const generateKeyset = useCallback((): KeysetPrivate => {
    return createNewKeyset({ denominationCount: 32, unit: 'piconero' });
  }, []);

  /** Step 4: Publish mint announcement to Nostr */
  const publishAnnouncement = useCallback(async (
    name: string,
    description: string,
    mintPrivkeyHex: string,
    keysets: KeysetPrivate[],
    depositFeePercent: number,
    withdrawFeeFlat: number,
    requiredConfirmations: number,
  ) => {
    const announcement: MintAnnouncement = {
      name,
      description,
      unit: 'piconero',
      keysets: keysets.map(k => ({
        id: k.id,
        unit: k.unit,
        pubkeys: k.pubkeys,
        createdAt: k.createdAt,
        active: k.active,
      })),
      fees: {
        depositPercent: depositFeePercent,
        withdrawFlat: withdrawFeeFlat,
      },
      requiredConfirmations,
      moneroNetwork: 'mainnet',
      version: '1.0',
    };

    const event = createMintAnnouncementEvent(announcement, mintPrivkeyHex);
    await nostr.event(event);
    return event;
  }, [nostr]);

  /** Complete all setup steps and persist */
  const finalizeSetup = useCallback(async (
    name: string,
    description: string,
    nsecHex: string,
    npubHex: string,
    moneroSeed: string,
    moneroAddress: string,
    moneroViewKey: string,
    moneroSpendKey: string,
    nodeUri: string,
    depositFeePercent: number,
    withdrawFeeFlat: number,
    requiredConfirmations: number,
    restoreHeight: number,
  ) => {
    setLoading(true);
    try {
      // Generate initial keyset
      const keyset = generateKeyset();

      // Save keyset
      await saveKeyset(keyset);

      // Save mint config
      await saveMintConfig({
        id: 'config',
        nsec: nsecHex,
        npub: npubHex,
        moneroSeed,
        moneroAddress,
        moneroViewKey,
        moneroSpendKey,
        moneroNodeUri: nodeUri,
        networkType: 'mainnet',
        depositFeePercent,
        withdrawFeeFlat,
        requiredConfirmations,
        maxDenomination: 32,
        setupComplete: false,
        restoreHeight,
      });

      // Publish announcement to Nostr
      const keysets = [keyset];
      await publishAnnouncement(name, description, nsecHex, keysets, depositFeePercent, withdrawFeeFlat, requiredConfirmations);

      // Mark setup as complete
      await completeMintSetup();
      setSetupComplete(true);
    } catch (err) {
      console.error('Mint setup failed:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [generateKeyset, publishAnnouncement]);

  return {
    loading,
    setupComplete,
    setupState,
    checkSetup,
    generateNostrIdentity,
    generateMoneroWallet,
    generateKeyset,
    finalizeSetup,
  };
}
